import React, { useEffect, useMemo, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import LoopIcon from '@mui/icons-material/Loop'
import { refreshConnectAccountData, getConnectOnboardingLink, resetOnboardingLink } from 'features/giving/givingSlice'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { useLocalizedFormatters } from 'i18n/useLocalizedFormatters'

export default function RequirementsDue() {
    const { t } = useTranslation('giving')
    const { formatDate } = useLocalizedFormatters()
    const dispatch = useDispatch()
    const [deadlineDateString, setDeadlineDateString] = useState('')

    const {
        connectAccount,
        refreshConnectAccountDataLoading,
        getConnectOnboardingLinkSuccess,
        getConnectOnboardingLinkError,
        getConnectOnboardingLinkMessage,
        getConnectOnboardingLinkLoading,
        onboardingLink,
    } = useSelector(state => state.giving)

    const requirements = useMemo(
        () => connectAccount?.requirements?.currently_due || [],
        [connectAccount]
    )

    useEffect(() => {
        const deadline = connectAccount?.requirements?.current_deadline
        if (!deadline) {
            setDeadlineDateString('')
            return
        }

        if (deadline > Number((Date.now() / 1000).toFixed(0))) {
            setDeadlineDateString(formatDate(deadline * 1000))
        } else {
            setDeadlineDateString('')
        }
    }, [connectAccount?.requirements?.current_deadline, formatDate])

    useEffect(() => {
        if (getConnectOnboardingLinkSuccess && onboardingLink) {
            window.open(onboardingLink, '_blank', 'noopener,noreferrer')
            dispatch(resetOnboardingLink())
        }
    }, [dispatch, getConnectOnboardingLinkSuccess, onboardingLink])

    useEffect(() => {
        if (getConnectOnboardingLinkError) {
            toast.error(getConnectOnboardingLinkMessage || t('admin.startError'))
        }
    }, [getConnectOnboardingLinkError, getConnectOnboardingLinkMessage])

    const handleContinueSetup = () => {
        dispatch(getConnectOnboardingLink())
    }

    const handleRefresh = () => {
        dispatch(resetOnboardingLink())
        dispatch(refreshConnectAccountData())
    }

    return (
        <RequirementsContainer>
            <StatusCard>
                <IconCircle>!</IconCircle>
                <StatusTitle>{t('admin.incompleteTitle')}</StatusTitle>
                <StatusCopy>
                    {t('admin.incompleteBody')}
                </StatusCopy>

                <StepsCard>
                    <Step><b>1.</b> {t('admin.stepRequirements')}</Step>
                    <Step><b>2.</b> {t('admin.stepVerification')}</Step>
                    <Step><b>3.</b> {t('admin.stepRefresh')}</Step>
                    {deadlineDateString ? (
                        <Deadline>{t('admin.deadline', { date: deadlineDateString })}</Deadline>
                    ) : null}
                </StepsCard>

                {requirements.length > 0 ? (
                    <RequirementList>
                        {requirements.map((item, index) => (
                            <RequirementItem key={`${item}-${index}`}>{formatRequirement(item)}</RequirementItem>
                        ))}
                    </RequirementList>
                ) : null}

                <Actions>
                    <SecondaryButton
                        type="button"
                        loading={refreshConnectAccountDataLoading}
                        onClick={handleRefresh}
                    >
                        <LoopIcon />
                        {refreshConnectAccountDataLoading ? t('admin.refreshing') : t('admin.refreshStatus')}
                    </SecondaryButton>
                    <PrimaryButton
                        type="button"
                        disabled={getConnectOnboardingLinkLoading}
                        onClick={handleContinueSetup}
                    >
                        {getConnectOnboardingLinkLoading ? t('admin.opening') : t('admin.continueSetup')}
                    </PrimaryButton>
                </Actions>
            </StatusCard>
        </RequirementsContainer>
    )
}

const formatRequirement = (value) => (
    String(value)
        .replace(/\./g, ' ')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
)

const SpinAction = keyframes`
    100% { transform: rotate(360deg); }
`

const RequirementsContainer = styled.div`
    width: 100%;
    padding: 20px;
    display: flex;
    justify-content: center;
`

const StatusCard = styled.div`
    width: min(100%, 760px);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 30px;
    border-radius: 18px;
    background: #ffffff;
    box-shadow: 0 12px 35px rgba(15, 23, 42, 0.1);
`

const IconCircle = styled.div`
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: rgba(245, 158, 11, 0.12);
    color: #f59e0b;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 900;
    font-size: 28px;
`

const StatusTitle = styled.h2`
    margin: 0;
    color: var(--lightThemeFontColor);
    font-size: 24px;
    font-weight: 700;
`

const StatusCopy = styled.div`
    max-width: 580px;
    color: #6b7280;
    font-size: 14px;
    line-height: 1.6;
    text-align: center;
`

const StepsCard = styled.div`
    width: 100%;
    border-radius: 14px;
    background: #f3f4f6;
    padding: 16px 18px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`

const Step = styled.div`
    color: #374151;
    font-size: 14px;
`

const Deadline = styled.div`
    margin-top: 4px;
    color: #92400e;
    font-size: 13px;
    font-weight: 700;
`

const RequirementList = styled.div`
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`

const RequirementItem = styled.div`
    border-radius: 999px;
    background: rgba(0, 180, 204, 0.1);
    color: var(--themeColor);
    font-size: 12px;
    font-weight: 700;
    padding: 8px 12px;
`

const Actions = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-end;
    gap: 12px;

    @media (max-width: 640px) {
        flex-direction: column-reverse;
    }
`

const BaseButton = styled.button`
    min-height: 44px;
    border-radius: 12px;
    padding: 0 18px;
    font-weight: 800;
    cursor: pointer;
`

const PrimaryButton = styled(BaseButton)`
    border: none;
    background: var(--themeColor);
    color: #ffffff;
    min-width: 190px;
    opacity: ${props => props.disabled ? 0.65 : 1};
    pointer-events: ${props => props.disabled ? 'none' : 'auto'};
`

const SecondaryButton = styled(BaseButton)`
    border: 1px solid #d1d5db;
    background: #ffffff;
    color: #4b5563;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    svg {
        font-size: 18px;
        animation-name: ${props => props.loading ? SpinAction : 'none'};
        animation-duration: 1s;
        animation-iteration-count: infinite;
        animation-timing-function: linear;
    }
`
